import React, { useContext, createContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import getConfigurationByKey from 'store/app/selector/getConfigurationByKey';
import isDesktop from 'store/app/selector/isDesktop';
import getLandingPageCategoryId from 'store/landing/selector/getLandingPageCategoryId';
import useLocale from 'hooks/useLocale';
import { Locale } from 'api/domains';
import { CatalogItem, GroupedCatalog, Theme } from 'store/landing/types';
import getLandingPageId from 'store/landing/selector/getLandingPageId';
import getTrustpilotData from 'store/app/selector/getTrustpilotData';
import { TrustpilotData } from 'store/app/types';
import getCatalogData from 'store/landing/selector/getCatalogData';
import useGermanNoteSign from 'hooks/useGermanNoteSign';
import fetchRecentlyViewedProductPricesOperation from 'store/product/operation/fetchRecentlyViewedProductPricesOperation';
import { ProductItemPreviewProps } from 'store/product/types';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction, Store } from 'redux';
import fetchCatalogItemsProductPricesOperation from 'store/product/operation/fetchCatalogItemsProductPricesOperation';
import addOPPlusSubscriptionAndRedirectToCartOperation from 'store/app/operation/addOPPlusSubscriptionAndRedirectToCartOperation';
import usePlusMembershipStatus, { MembershipStatus } from 'components/Plus/hooks/usePlusMembershipStatus';
import getLandingPageData from 'store/landing/selector/getLandingPageData';

type LandingPageProviderProps = {
  children: React.ReactNode;
};

type LandingPageContextType = {
  theme: Theme;
  isSearchBarEnabled: boolean;
  isDesktopDevice: boolean;
  categoryId: number | null;
  locale: Locale;
  landingPageId?: string;
  trustpilotData?: TrustpilotData;
  groupedCatalogItems?: GroupedCatalog[];
  germanNoteSign?: string;
  fetchRecentlyViewedPrices: (
    items: ProductItemPreviewProps[],
  ) => (dispatch: ThunkDispatch<any, any, AnyAction>) => Promise<void>;
  fetchCatalogItemsPrices: (
    items: CatalogItem[],
  ) => (dispatch: ThunkDispatch<any, any, AnyAction>, getState: () => Store) => Promise<void>;
  addOpPlus: (
    showMessage: boolean,
    redirectUrl?: string,
  ) => (dispatch: ThunkDispatch<any, any, AnyAction>) => Promise<void>;
  plusMembershipStatus: MembershipStatus;
};

export const LandingPageContext = createContext({} as LandingPageContextType);

export const LandingPageProvider = ({ children }: LandingPageProviderProps) => {
  const theme = useSelector(getLandingPageData)?.theme || 'default';
  const isDesktopDevice = useSelector(isDesktop);
  const categoryId = useSelector(getLandingPageCategoryId);
  const isSearchBarEnabled = useSelector(getConfigurationByKey('isGettySearchEnabled'), Boolean);
  const locale = useLocale();
  const landingPageId = useSelector(getLandingPageId);
  const trustpilotData = useSelector(getTrustpilotData);
  const groupedCatalogItems = useSelector(getCatalogData);
  const germanNoteSign = useGermanNoteSign();
  const dispatch = useDispatch();
  const fetchRecentlyViewedPrices = (items: ProductItemPreviewProps[]) =>
    dispatch(fetchRecentlyViewedProductPricesOperation(items));
  const fetchCatalogItemsPrices = (items: CatalogItem[]) => dispatch(fetchCatalogItemsProductPricesOperation(items));
  const addOpPlus = (showMessage: boolean, redirectUrl?: string) =>
    dispatch(addOPPlusSubscriptionAndRedirectToCartOperation(showMessage, redirectUrl));
  const plusMembershipStatus = usePlusMembershipStatus();

  return (
    <LandingPageContext.Provider
      value={{
        theme,
        isSearchBarEnabled,
        isDesktopDevice,
        categoryId,
        locale,
        landingPageId,
        trustpilotData,
        groupedCatalogItems,
        germanNoteSign,
        fetchRecentlyViewedPrices,
        fetchCatalogItemsPrices,
        addOpPlus,
        plusMembershipStatus,
      }}
    >
      {children}
    </LandingPageContext.Provider>
  );
};

export const useLandingPageContext = () => {
  const context = useContext(LandingPageContext);
  const {
    theme,
    isSearchBarEnabled,
    isDesktopDevice,
    categoryId,
    locale,
    landingPageId,
    trustpilotData,
    groupedCatalogItems,
    germanNoteSign,
    fetchRecentlyViewedPrices,
    fetchCatalogItemsPrices,
    addOpPlus,
    plusMembershipStatus,
  } = context;
  return {
    theme,
    isSearchBarEnabled,
    isDesktopDevice,
    categoryId,
    locale,
    landingPageId,
    trustpilotData,
    groupedCatalogItems,
    germanNoteSign,
    fetchRecentlyViewedPrices,
    fetchCatalogItemsPrices,
    addOpPlus,
    plusMembershipStatus,
  };
};
